/* prettier-ignore-file */
import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Modal,
  ActivityIndicator,
} from "react-native";
import { useParams, useNavigate } from "react-router-dom";
import { useBookings, useCheckInStore, useAuthStore } from "../stores";
import { formatPhoneNumberDashes as formatPhoneNumber, capitalizeWords } from "../utils/formatting";

const AllMedia = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  
  // Get booking from stores
  const bookings = useBookings((state) => state.bookings);
  const checkInBooking = useCheckInStore((state) => state.booking);
  
  // Find booking - check both stores
  const booking = useMemo(() => {
    if (!bookingId) return null;
    
    // First check regular bookings store
    const foundBooking = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
    if (foundBooking) return foundBooking;
    
    // Then check check-in store
    if (checkInBooking && (checkInBooking.id === bookingId || checkInBooking.bookingId === bookingId)) {
      return checkInBooking;
    }
    
    return null;
  }, [bookings, checkInBooking, bookingId]);

  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showSections, setShowSections] = useState({
    outbound: true,
    checkIn: true,
    messages: true,
  });

  // Extract media arrays
  const outboundMedia = booking?.media?.outbound || [];
  const checkInMedia = booking?.media?.checkIn || [];
  const messagesMedia = booking?.media?.messages || [];

  // Get customer info
  const customerName = booking 
    ? capitalizeWords(`${booking.firstName || ''} ${booking.lastName || ''}`.trim()) || 'Customer'
    : '';
  const customerPhone = booking?.customerPhone || '';
  const customerBookingID = booking?.customerBookingID || '';

  // Toggle section visibility
  const toggleSection = (section) => {
    setShowSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Open media in full screen
  const openMedia = (mediaItem) => {
    setSelectedMedia(mediaItem);
  };

  // Close media viewer
  const closeMedia = () => {
    setSelectedMedia(null);
  };

  // Navigate to messages
  const handlePhoneClick = () => {
    if (!customerPhone) return;
    
    const firstName = booking?.firstName ? booking.firstName.charAt(0).toUpperCase() + booking.firstName.slice(1).toLowerCase() : '';
    const lastName = booking?.lastName ? booking.lastName.charAt(0).toUpperCase() + booking.lastName.slice(1).toLowerCase() : '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Customer';
    
    navigate("/messages", {
      state: {
        customer: {
          phone: customerPhone,
          fullName: fullName
        }
      }
    });
  };

  // Handle desktop resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!booking) {
    return (
      <View style={styles.container}>
        <TouchableOpacity dataSet={{ hover: "lift" }} 
          style={[
            styles.floatingBackButton,
            isDesktop && styles.floatingBackButtonDesktop
          ]} 
          onPress={() => navigate(-1)}
        >
          <Text style={styles.floatingBackButtonText}>←</Text>
        </TouchableOpacity>
        <View style={isDesktop ? styles.desktopWrapper : null}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.loadingText}>Loading booking...</Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Floating Back Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }} 
        style={[
          styles.floatingBackButton,
          isDesktop && styles.floatingBackButtonDesktop
        ]} 
        onPress={() => navigate(-1)}
      >
        <Text style={styles.floatingBackButtonText}>←</Text>
      </TouchableOpacity>

      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Customer Info Container */}
        <View style={styles.customerInfoContainer}>
          <Text style={styles.customerName}>{customerName}</Text>
          <TouchableOpacity onPress={handlePhoneClick}>
            <Text style={styles.customerPhone}>{formatPhoneNumber(customerPhone)}</Text>
          </TouchableOpacity>
          <Text style={styles.bookingId}>Booking ID: {customerBookingID}</Text>
        </View>

        {/* Going Out Section */}
        <View style={styles.section}>
          <TouchableOpacity 
            style={styles.sectionHeader}
            onPress={() => toggleSection('outbound')}
          >
            <Text style={styles.sectionTitle}>Check Out</Text>
            <Text style={styles.sectionToggle}>
              {showSections.outbound ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>
          
          {showSections.outbound && (
            <View style={styles.mediaGrid}>
              {outboundMedia.length === 0 ? (
                <Text style={styles.emptyText}>No media</Text>
              ) : (
                outboundMedia.map((item, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.mediaThumbnail}
                    onPress={() => openMedia(item)}
                  >
                    {item.type === 'video' ? (
                      <View style={styles.videoThumbnail}>
                        <Image
                          source={{ uri: item.url }}
                          style={styles.thumbnailImage}
                          resizeMode="cover"
                        />
                        <View style={styles.playIcon}>
                          <Text style={styles.playIconText}>▶</Text>
                        </View>
                      </View>
                    ) : (
                      <Image
                        source={{ uri: item.url }}
                        style={styles.thumbnailImage}
                        resizeMode="cover"
                      />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </View>
          )}
        </View>

        {/* Check-In Section */}
        <View style={styles.section}>
          <TouchableOpacity 
            style={styles.sectionHeader}
            onPress={() => toggleSection('checkIn')}
          >
            <Text style={styles.sectionTitle}>Check-In</Text>
            <Text style={styles.sectionToggle}>
              {showSections.checkIn ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>
          
          {showSections.checkIn && (
            <View style={styles.mediaGrid}>
              {checkInMedia.length === 0 ? (
                <Text style={styles.emptyText}>No media</Text>
              ) : (
                checkInMedia.map((item, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.mediaThumbnail}
                    onPress={() => openMedia(item)}
                  >
                    {item.type === 'video' ? (
                      <View style={styles.videoThumbnail}>
                        <Image
                          source={{ uri: item.url }}
                          style={styles.thumbnailImage}
                          resizeMode="cover"
                        />
                        <View style={styles.playIcon}>
                          <Text style={styles.playIconText}>▶</Text>
                        </View>
                      </View>
                    ) : (
                      <Image
                        source={{ uri: item.url }}
                        style={styles.thumbnailImage}
                        resizeMode="cover"
                      />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </View>
          )}
        </View>

        {/* Chat Section */}
        <View style={styles.section}>
          <TouchableOpacity 
            style={styles.sectionHeader}
            onPress={() => toggleSection('messages')}
          >
            <Text style={styles.sectionTitle}>Chat</Text>
            <Text style={styles.sectionToggle}>
              {showSections.messages ? '▼' : '▶'}
            </Text>
          </TouchableOpacity>
          
          {showSections.messages && (
            <View style={styles.mediaGrid}>
              {messagesMedia.length === 0 ? (
                <Text style={styles.emptyText}>No media</Text>
              ) : (
                messagesMedia.map((item, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.mediaThumbnail}
                    onPress={() => openMedia(item)}
                  >
                    {item.type === 'video' ? (
                      <View style={styles.videoThumbnail}>
                        <Image
                          source={{ uri: item.url }}
                          style={styles.thumbnailImage}
                          resizeMode="cover"
                        />
                        <View style={styles.playIcon}>
                          <Text style={styles.playIconText}>▶</Text>
                        </View>
                      </View>
                    ) : (
                      <Image
                        source={{ uri: item.url }}
                        style={styles.thumbnailImage}
                        resizeMode="cover"
                      />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </View>
          )}
        </View>
      </ScrollView>
      </View>

      {/* Full Screen Media Viewer Modal */}
      <Modal
        visible={selectedMedia !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={closeMedia}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            {selectedMedia?.type === 'video' ? (
              <View style={styles.fullMediaVideoContainer}>
                <video
                  src={selectedMedia.url}
                  controls
                  autoPlay
                  style={styles.fullScreenMedia}
                />
              </View>
            ) : (
              <Image
                source={{ uri: selectedMedia?.url }}
                style={styles.fullScreenMedia}
                resizeMode="contain"
              />
            )}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.closeButton}
              onPress={closeMedia}
            >
              <Text style={styles.closeButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  floatingBackButton: {
    position: "absolute",
    top: 20,
    left: 20,
    width: 50,
    height: 50,
    backgroundColor: "rgba(0, 122, 255, 0.5)",
    borderRadius: 25,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  floatingBackButtonDesktop: {
    left: "calc(50% - 400px + 20px)",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  floatingBackButtonText: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: "bold",
    lineHeight: 28,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingTop: 60,
  },
  customerInfoContainer: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 6,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  customerName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  customerPhone: {
    fontSize: 16,
    color: "#0E7C6B",
    marginBottom: 8,
    textDecorationLine: "underline",
  },
  bookingId: {
    fontSize: 14,
    color: "#5C6B68",
  },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
    overflow: "hidden",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
  },
  sectionToggle: {
    fontSize: 16,
    color: "#5C6B68",
  },
  mediaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    padding: 8,
  },
  mediaThumbnail: {
    width: "31%",
    aspectRatio: 1,
    margin: "1%",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#DDE3E2",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  videoThumbnail: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  playIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: [{ translateX: -15 }, { translateY: -15 }],
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  playIconText: {
    color: "#FFFFFF",
    fontSize: 12,
    marginLeft: 2,
  },
  emptyText: {
    padding: 16,
    color: "#8A9794",
    fontSize: 14,
    fontStyle: "italic",
    width: "100%",
    textAlign: "center",
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.95)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  fullScreenMedia: {
    width: "100%",
    height: "80%",
    maxWidth: "100%",
    maxHeight: "80%",
  },
  fullMediaVideoContainer: {
    width: "100%",
    height: "80%",
    justifyContent: "center",
    alignItems: "center",
  },
  closeButton: {
    position: "absolute",
    bottom: 40,
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 16,
  },
  closeButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
});

export default AllMedia;

